<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { computed, onMounted, ref } from 'vue'

import { Grid } from '@progress/kendo-vue-grid';
import { Checkbox as KCheckBox, type CheckboxChangeEvent } from '@progress/kendo-vue-inputs'
import { SvgIcon } from '@progress/kendo-vue-common'
import { circleIcon } from '@progress/kendo-svg-icons'
import Swal from 'sweetalert2'

import { useLogger } from 'vue-logger-plugin'

import type { IAoeCart } from '@/types/CartTypes'
import type { IEventSubmittedOrder } from '@/types/EventTypes'

import { UpdateCartActive } from '@/services/CartService'
import { FormatCurrency } from '@/services/currencyFormatter'
import {
  GetCartProductSubtotal,
  GetCartTotalDiscountAmount,
} from '@/services/CartTotalsService'
import { GetOrderReceiptUrl } from '@/stores/SettingsStore.ts'
import { resolveColumnWidth } from '@/utils/kendoHelper.ts'

const logger = useLogger()

const emit = defineEmits(["update-cart"]);

/**
 * One grid row. Active carts and already submitted orders share a single row
 * shape so the money columns and the footer totals read the same fields for
 * both - a submitted order just has no cart to activate and no stock to warn
 * about, so those cells render empty.
 */
interface ISummaryRow {
  rowKey: string
  isSubmitted: boolean
  aoeCartIdToken: string | null
  aoeOrderId: number | null
  aoeOrderIdToken: string | null
  isActive: boolean
  customerFirstName: string
  customerLastName: string
  stockIndicator: string | null
  productSubtotal: number
  totalDiscountAmount: number
  shippingCost: number
  total: number
  paymentTotal: number
  balance: number
}

const orderReceiptURL = ref<string>('')
const columns = [
        {
          field: 'activateCart',
          width: resolveColumnWidth('80px', t('partyOrderEntry.ActiveCartHeader')),
          title: t('partyOrderEntry.ActiveCartHeader'),
          cell: 'activateCartCell',
        },
        {
          field: 'customerName',
          title: t('partyOrderEntry.CustomerNameHeader'),
          cell: 'customerNameCell',
          width: resolveColumnWidth('180px', t('partyOrderEntry.CustomerNameHeader'))
        },
        {
          field: 'aoeOrderId',
          title: t('partyOrderEntry.OrderNumberHeader'),
          cell: 'orderNumberCell',
          width: resolveColumnWidth('100px', t('partyOrderEntry.OrderNumberHeader'))
        },
        {
          field: 'stockIndicator',
          title: t('partyOrderEntry.PSHeader'),
          headerClassName: 'order-grid-header-stock',
          cell: 'stockIndicatorCell',
          width: resolveColumnWidth('70px', t('partyOrderEntry.PSHeader'))
        },
        {
          field: 'productSubtotal',
          title: t('partyOrderEntry.SubTotalHeader'),
          cell: 'productSubtotalCell',
          width: resolveColumnWidth('90px', t('partyOrderEntry.SubTotalHeader')),
        },
        {
          field: 'totalDiscountAmount',
          title: t('partyOrderEntry.DiscountHeader'),
          cell: 'totalDiscountAmountCell',
          width: resolveColumnWidth('90px', t('partyOrderEntry.DiscountHeader')),
          footerCell: 'footerTemplate'
        },
        {
          field: 'shippingCost',
          title: t('partyOrderEntry.ShippingCostHeader'),
          cell: 'shippingCostCell',
          width: resolveColumnWidth('120px', t('partyOrderEntry.ShippingCostHeader'))
        },
        {
          field: 'total',
          title: t('partyOrderEntry.TotalHeader'),
          cell: 'totalCell',
          width: resolveColumnWidth('90px', t('partyOrderEntry.TotalHeader')),
          footerCell: 'footerTemplateTotal'
        },
        {
          field: 'paymentTotal',
          title: t('partyOrderEntry.PaymentHeader'),
          cell: 'paymentTotalCell',
          width: resolveColumnWidth('90px', t('partyOrderEntry.PaymentHeader')),
          footerCell: 'footerTemplatePaymentTotal'
        },
        {
          field: 'balance',
          title: t('partyOrderEntry.BalanceHeader'),
          cell: 'balanceCell',
          width: resolveColumnWidth('90px', t('partyOrderEntry.BalanceHeader')),
          footerCell: 'footerTemplateBalanceTotal'
        },
      ]

const props = defineProps<{
  griddataitems: IAoeCart[]
  disableactivate: boolean
  submittedorders?: IEventSubmittedOrder[]
  loadingsubmittedorders?: boolean
}>()

const disableActivateCheck = ref<boolean>(props.disableactivate)

onMounted(async () => {
  orderReceiptURL.value = await GetOrderReceiptUrl()
})

/** Active carts first, then the orders already submitted against the event. */
const rows = computed<ISummaryRow[]>(() => {
  const cartRows: ISummaryRow[] = props.griddataitems
    .filter(cart => cart.isActive)
    .map(cart => ({
      rowKey: 'cart-' + cart.aoeCartIdToken,
      isSubmitted: false,
      aoeCartIdToken: cart.aoeCartIdToken,
      aoeOrderId: null,
      aoeOrderIdToken: null,
      isActive: cart.isActive,
      customerFirstName: cart.customerFirstName,
      customerLastName: cart.customerLastName,
      stockIndicator: GetCartStockIndicator(cart),
      productSubtotal: GetCartProductSubtotal(cart),
      totalDiscountAmount: GetCartTotalDiscountAmount(cart),
      shippingCost: cart.shippingCost,
      total: cart.total,
      paymentTotal: cart.paymentTotal,
      balance: cart.balance,
    }))

  const submittedRows: ISummaryRow[] = (props.submittedorders ?? []).map(order => ({
    rowKey: 'order-' + order.aoeOrderIdToken,
    isSubmitted: true,
    aoeCartIdToken: null,
    aoeOrderId: order.aoeOrderId,
    aoeOrderIdToken: order.aoeOrderIdToken,
    isActive: true,
    customerFirstName: order.customerFirstName,
    customerLastName: order.customerLastName,
    stockIndicator: null,
    productSubtotal: order.productSubtotal,
    totalDiscountAmount: order.totalDiscountAmount,
    shippingCost: order.shippingCost,
    total: order.total,
    paymentTotal: order.paymentTotal,
    balance: order.balance,
  }))

  return [...cartRows, ...submittedRows]
})

function GetGuestCartsTotal() {
  return rows.value.reduce((acc, row) => acc + row.total, 0)
}

function GetGuestCartsPaymentTotal() {
  return rows.value.reduce((acc, row) => acc + row.paymentTotal, 0)
}

function GetGuestCartsBalanceTotal() {
  return rows.value.reduce((acc, row) => acc + row.balance, 0)
}

function activateCartCheckboxChange(event: CheckboxChangeEvent, aoeCartIdToken: string) {
  logger.debug('activateCartCheckboxChange', aoeCartIdToken, event)

  // If unchecking (deactivating) the cart, show confirmation dialog
  if (!event.value) {
    Swal.fire({
      title: t('partyOrderEntry.InactivatingCartTitle'),
      text: t('partyOrderEntry.InactivatingCartMessage') + '\n\n' + t('partyOrderEntry.InactivatingCartConfirm'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('buttons.Yes'),
      cancelButtonText: t('buttons.No'),
    }).then(result => {
      if (result.isConfirmed) {
        UpdateCartActive({
          aoeCartIdToken: aoeCartIdToken,
          active: event.value
        }).then(() => {
          emit("update-cart", event)
        })
      }
      // If cancelled, the checkbox will remain checked (no action needed)
    })
    return
  }

  // If checking (activating) the cart, proceed directly
  UpdateCartActive({
        aoeCartIdToken: aoeCartIdToken,
        active: event.value
    }).then(() => {
      emit("update-cart", event)
    })
}

function GetCartStockIndicator(cart: IAoeCart) : string {
  let rval = 'Available'
  for (const item of cart.items)
  {
    if (item.inventoryLevel != null)
    {
      // If OutOfStockVisible is true, treat item as available
      if (item.outOfStockVisible === true) {
        continue
      }

      if (item.inventoryLevel.inventoryLevel == 'LowStock' && rval != 'OutOfStock')
      {
        rval = 'LowStock'
      }
      else if (item.inventoryLevel.inventoryLevel == 'OutOfStock')
      {
        rval = 'OutOfStock'
      }
      else if (item.inventoryLevel.inventoryLevel == 'StopSell')
      {
        return 'StopSell'
      }
    }
  }
  return rval;
}

function formatCurrency(value : number) {
  // Use the centralized FormatCurrency function from currencyFormatter.ts
  // Get the first active cart to determine currency
  const firstActiveCart = props.griddataitems.find(cart => cart.isActive);
  const currencyId = firstActiveCart?.currencyId || null
  return FormatCurrency(value, currencyId)
}

</script>

<template>
    <Grid
      ref="gridCartItems"
      :data-items="rows"
      :data-item-key="'rowKey'"
      :resizable="true"
      :pageable="false"
      class="guest-orders-grid"
      :columns="columns"
      :loader="props.loadingsubmittedorders ?? false">
      <template v-slot:activateCartCell="{ props }">
        <td v-if="props.rowType !== 'groupHeader'" class="order-grid-activate text-center">
          <KCheckBox
            v-if="!props.dataItem.isSubmitted"
            :checked="props.dataItem.isActive"
            :disabled="disableActivateCheck"
            @change="activateCartCheckboxChange($event, props.dataItem.aoeCartIdToken)"
          />
        </td>
      </template>
      <template v-slot:customerNameCell="{ props }">
        <td v-if="props.rowType !== 'groupHeader'" class="order-grid-name">
          {{ props.dataItem.customerFirstName }} {{ props.dataItem.customerLastName }}
        </td>
      </template>
      <template v-slot:orderNumberCell="{ props }">
        <td v-if="props.rowType !== 'groupHeader'" class="order-grid-order-number">
          <template v-if="props.dataItem.isSubmitted">
            <a
              v-if="orderReceiptURL.length > 0"
              class="base-hyper-link order-grid-order-number-link"
              :href="orderReceiptURL + '?id=' + props.dataItem.aoeOrderIdToken"
              :title="t('partyOrderEntry.ViewOrderReceipt')">
              {{ props.dataItem.aoeOrderId }}
            </a>
            <span v-else>{{ props.dataItem.aoeOrderId }}</span>
          </template>
        </td>
      </template>
      <template v-slot:stockIndicatorCell="{ props }">
        <td v-if="props.rowType !== 'groupHeader'" class="product-grid-stock-indicator">
          <SvgIcon
            v-if="props.dataItem.stockIndicator == 'Available'"
            class="product-stock-indicator inventory-available"
            :icon="circleIcon">
          </SvgIcon>
          <SvgIcon
            v-if="props.dataItem.stockIndicator == 'LowStock'"
            class="product-stock-indicator inventory-low-stock"
            :icon="circleIcon">
          </SvgIcon>
          <SvgIcon
            v-if="props.dataItem.stockIndicator == 'OutOfStock'"
            class="product-stock-indicator inventory-out-of-stock"
            :icon="circleIcon">
          </SvgIcon>
          <SvgIcon
            v-if="props.dataItem.stockIndicator == 'StopSell'"
            class="product-stock-indicator inventory-stop-sell"
            :icon="circleIcon">
          </SvgIcon>
        </td>
      </template>
      <template v-slot:productSubtotalCell="{ props }">
        <td v-if="props.rowType !== 'groupHeader'">
          {{ formatCurrency(props.dataItem.productSubtotal) }}
        </td>
      </template>
      <template v-slot:totalDiscountAmountCell="{ props }">
        <td v-if="props.rowType !== 'groupHeader'">
          {{ formatCurrency(props.dataItem.totalDiscountAmount) }}
        </td>
      </template>
      <template v-slot:shippingCostCell="{ props }">
        <td v-if="props.rowType !== 'groupHeader'">
          {{ formatCurrency(props.dataItem.shippingCost) }}
        </td>
      </template>
      <template v-slot:totalCell="{ props }">
        <td v-if="props.rowType !== 'groupHeader'">
          {{ formatCurrency(props.dataItem.total) }}
        </td>
      </template>
      <template v-slot:paymentTotalCell="{ props }">
        <td v-if="props.rowType !== 'groupHeader'">
          {{ formatCurrency(props.dataItem.paymentTotal) }}
        </td>
      </template>
      <template v-slot:balanceCell="{ props }">
        <td v-if="props.rowType !== 'groupHeader'">
          {{ formatCurrency(props.dataItem.balance) }}
        </td>
      </template>
      <template v-slot:footerTemplate>
          <div>{{ t('partyOrderEntry.GridFooterTotal') }}</div>
      </template>
      <template v-slot:footerTemplateTotal>
          <div class="cart-item-grid-footer-total">{{ formatCurrency(GetGuestCartsTotal()) }} </div>
      </template>
      <template v-slot:footerTemplatePaymentTotal>
          <div class="cart-item-grid-footer-payment">{{ formatCurrency(GetGuestCartsPaymentTotal()) }} </div>
      </template>
      <template v-slot:footerTemplateBalanceTotal>
          <div class="cart-item-grid-footer-subtotal">{{ formatCurrency(GetGuestCartsBalanceTotal()) }} </div>
      </template>
    </Grid>
</template>

<style lang="scss">

.order-grid-header-stock .k-column-title
{
  display: none;
}

.aoe-party-order-summary-control .k-grid-content
{
  overflow-y: hidden;
}

.aoe-party-order-summary-control .k-grid-header
{
  padding: inherit !important;
  padding-inline-end: inherit;
}

</style>
